import React, {useState, useEffect} from 'react'
import moment from 'moment'
import SearchResident from '../SearchResident'
import SelectInput from '../SelectInput'
import DateInput from './../DateInput'
import NumberInput from '../NumberInput'
import {Grid, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {H4HeaderWithoutBold} from '../../assets/styles/typography'
import {rxrRedColor, rxrContainerColor, rxrGreyColor, rxrPrimaryColor} from '../../assets/styles/color'
import {spaceMedium, spaceSmall, spaceExtraExtraSmall} from '../../assets/styles/spacing'
import MultilineTextInput from '../MultilineTextInput'
import {useSelector} from 'react-redux'
import PropTypes from 'prop-types'
import RXRButton from '../RXRButton'
import Constant from './AmenitiesConstants'
import ToggleButtons from '../ToggleButtons'
import DiscardChangesDialog from '../DiscardChangesDialog'
import RXRIcon from '../RXRIcon'
import ConfirmDeleteDialog from '../ConfirmDeleteDialog'
import TextInput from '../TextInput'
import {reservationNeedsApproval, reservationIsUpcoming, reservationIsPast, reservationIsUnderway} from './ViewAmenityReservationsPage'
import useAmenityReservationScheduling from './useAmenityReservationScheduling'
import AmenityReservationStatusComponent from './AmenityReservationStatusComponent'
import SelectPaymentMethodsForResident from '../Services/SelectPaymentMethodsForResident'
import SimpleSpinner from '../SimpleSpinner'
import BookingPriceBreakdown from './BookingPriceBreakdown'
import AmenityReservationChargeFlow from './AmenityReservationChargeFlow'

const useStyles = makeStyles({
  container: {
    flexGrow: 1,
    marginBottom: 22,
  },
  userTypeToggleContainer: {
    width: 240,
  },
  errorText: {
    marginTop: spaceMedium,
    color: rxrRedColor,
  },
  statusContainer: {
    marginBottom: spaceMedium,
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: `${spaceMedium}px 0`,
  },
  systemControlledMessage: {
    ...H4HeaderWithoutBold,
    color: rxrRedColor,
    marginBottom: spaceMedium,
  },
  ctaContainer: {
    marginTop: spaceMedium,
    display: 'flex',
    gap: spaceMedium,
  },
})

function AmenityReservationEditor(props) {
  const classes = useStyles()
  const [timeRanges, setTimeRanges] = useState([])
  const [endTimeRanges, setEndTimeRanges] = useState([])
  const [showDenyRequestDialog, setShowDenyRequestDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const amenitiesLookup = useSelector(state => state.Amenities.amenitiesLookup)

  const {
    isEditMode,
    form,
    setForm,
    focusedReservation,
    formChanged,
    invalidItems,
    amenityData,
    isLoadingCalendar,
    isSaving,
    saveError,
    isLoadingAmenityDetails,
    saveOrApprove,
    makeReservation,
    makeReservationAndApprove,
    handleReservationDate,
    loadMonthData,
    shouldDisableDate,
    deleteRequest,
    resetForm,
    isFormComplete,
    updateRequest,
    reservationDate,
    isPlacingStaffReservation,
    setIsPlacingStaffReservation,
    chatResident,
    denyRequest,
    shouldShowSaveAndApproveButton,
  } = useAmenityReservationScheduling(props, nextTimeRanges => {
    setTimeRanges(nextTimeRanges)

    // this ensures the start and end time are properly seeded when editing an existing reservation
    if (nextTimeRanges.length > 0 && form.startAt) {
      handleStartTimeChange(form.startAt, form.endAt, nextTimeRanges)
    } else {
      setEndTimeRanges([])
    }
  })

  const handleStartTimeChange = (startDate, endDate, timeRangeOverride) => {
    // we always set the end time to null when start time is changed
    setForm({startAt: new Date(startDate), endAt: endDate || null})
    const latestOption = moment(startDate).add(amenityData.maxReservationDuration, 'm').toISOString()
    const earliestOption = moment(startDate).add(amenityData.minReservationDuration, 'm').toISOString()
    let e = []
    e = (timeRangeOverride || timeRanges).filter(t => {
      return t.value >= earliestOption && t.value <= latestOption
    })

    setEndTimeRanges(e)
  }

  const handleEndTimeChange = date => {
    setForm({endAt: new Date(date)})
  }

  const handleChangeGuestCount = guests => {
    // + 1 for the resident
    setForm({partySize: guests + 1})
  }

  const hasNecessaryDataToSelectNumGuests =
    (!isPlacingStaffReservation && form.residentId && amenityData) || (isPlacingStaffReservation && amenityData)
  const filteredAmenities =
    typeof props.amenitiesFilter === 'function'
      ? Object.values(amenitiesLookup).filter(props.amenitiesFilter)
      : Object.values(amenitiesLookup)
  const amenitiesOptions = filteredAmenities.map(a => ({
    value: a.id,
    label: a.label,
  }))
  const isReservationSystemControlled = !!focusedReservation && focusedReservation.isSystemControlled
  const isReservationPast = !!focusedReservation && reservationIsPast(focusedReservation)
  const isReservationUnderway = !!focusedReservation && reservationIsUnderway(focusedReservation)
  const isStartAtDisabled = !!(!form.partySize || !reservationDate || isReservationSystemControlled || isReservationPast)
  const isEndAtDisabled = !!(isStartAtDisabled || !form.startAt || isReservationSystemControlled || isReservationPast)
  const showCostBreakdown = (amenityData && amenityData.requiresPayment) || form.bookingCost

  const useSimplifiedChargeFlow = focusedReservation && focusedReservation.readyForPayment

  return useSimplifiedChargeFlow ? (
    <AmenityReservationChargeFlow amenityReservationId={focusedReservation.id} />
  ) : (
    <React.Fragment>
      {isEditMode && form.reservationState && (
        <div className={classes.statusContainer}>
          <AmenityReservationStatusComponent state={form.reservationState} updatedAt={focusedReservation && focusedReservation.updatedAt} />
        </div>
      )}
      <Grid container spacing={3}>
        {isReservationSystemControlled && (
          <Grid item xs={12}>
            <Typography className={classes.systemControlledMessage}>
              This reservation is controlled by the system and cannot be modified
            </Typography>
          </Grid>
        )}

        {!isEditMode ? (
          <Grid item xs={12}>
            <div className={classes.userTypeToggleContainer}>
              <ToggleButtons
                selected={isPlacingStaffReservation ? 1 : 0}
                buttonLabels={['Resident', 'Staff']}
                onChange={v => setIsPlacingStaffReservation(!!v)}
                inverseColors={true}
              />
            </div>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <SelectInput
            onChange={id => setForm({amenityId: id})}
            label={'Amenity'}
            isRequired={true}
            options={amenitiesOptions}
            value={form.amenityId}
            disabled={!!(isEditMode || (props.initialValues && props.initialValues.amenityId))}
            error={invalidItems.includes('amenityId')}
          />
        </Grid>
        <Grid item xs={12}>
          {!isPlacingStaffReservation ? (
            <SearchResident
              value={form.residentId}
              isRequired={true}
              disabled={isEditMode}
              onSelectResident={r => setForm({residentId: r ? r.id : null})}
              error={invalidItems.includes('residentId')}
              includeSisterBuildings={true}
            />
          ) : (
            <TextInput
              isRequired={true}
              error={invalidItems.includes('title')}
              label="Title"
              value={form.title}
              onChange={val => setForm({title: val})}
            />
          )}
        </Grid>

        <Grid item md={6} xs={12}>
          <NumberInput
            label="Number of guests"
            onChange={handleChangeGuestCount}
            helperText={amenityData ? `Max guests per booking is ${amenityData.maxPartySize - 1}` : ''}
            max={amenityData ? amenityData.maxPartySize - 1 : 0}
            min={0}
            value={typeof form.partySize === 'number' ? form.partySize - 1 : null}
            isDisabled={!hasNecessaryDataToSelectNumGuests || isReservationSystemControlled || isReservationPast}
            isLoading={isLoadingAmenityDetails}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <DateInput
            onChange={handleReservationDate}
            onMonthChange={loadMonthData}
            shouldDisableDate={shouldDisableDate}
            min={moment().startOf('day').toDate()}
            label={'Reservation date'}
            isRequired={true}
            value={reservationDate}
            disabled={!hasNecessaryDataToSelectNumGuests || isReservationSystemControlled || isReservationPast || isReservationUnderway}
            error={invalidItems.includes('startAt')}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          {isReservationPast || isReservationUnderway ? (
            <TextInput isDisabled={true} value={moment(form.startAt).format('hh:mm A')} label={'Start time'} />
          ) : (
            <SelectInput
              onChange={handleStartTimeChange}
              label={'Start time'}
              isRequired={true}
              options={timeRanges.filter(time => time.canStart)}
              value={form.startAt ? form.startAt.toISOString() : ''}
              disabled={isStartAtDisabled}
              error={invalidItems.includes('startAt')}
              isLoading={isLoadingCalendar}
            />
          )}
        </Grid>
        <Grid item md={6} xs={12}>
          {isReservationPast ? (
            <TextInput isDisabled={true} value={moment(form.endAt).format('hh:mm A')} label={'End time'} />
          ) : (
            <SelectInput
              onChange={handleEndTimeChange}
              label={'End time'}
              isRequired={true}
              options={endTimeRanges}
              value={form.endAt ? form.endAt.toISOString() : ''}
              disabled={isEndAtDisabled}
              helperText={amenityData ? `Max booking duration is ${amenityData.maxReservationDuration} min` : ''}
              error={invalidItems.includes('endAt')}
              isLoading={isLoadingCalendar}
            />
          )}
        </Grid>
        {isLoadingAmenityDetails ? (
          <Grid item xs={12}>
            <div className={classes.spinnerContainer}>
              <SimpleSpinner />
            </div>
          </Grid>
        ) : (
          <>
            {amenityData && amenityData.requiresPayment && !isPlacingStaffReservation && (
              <Grid item xs={12}>
                <SelectPaymentMethodsForResident
                  residentId={form.residentId}
                  onSelect={paymentMethodId => setForm({paymentMethodId: paymentMethodId || null})}
                  paymentMethodId={form.paymentMethodId}
                  isDisabled={
                    !form.residentId ||
                    isReservationSystemControlled ||
                    isReservationPast ||
                    form.reservationState === Constant.STATE_DECLINED
                  }
                  hasError={invalidItems.includes('paymentMethodId')}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <MultilineTextInput
                label="Notes"
                placeholder=""
                rows={4}
                rowsMax={4}
                isRequired={false}
                maxLength={2000}
                onChange={val => setForm({notes: val})}
                isDisabled={isReservationSystemControlled || isReservationPast}
                value={form.notes}
                error={invalidItems.includes('notes')}
              />
            </Grid>
            {showCostBreakdown && form.startAt && form.endAt && !isPlacingStaffReservation && (
              <Grid item xs={12}>
                <BookingPriceBreakdown
                  amenityId={form.amenityId}
                  residentId={form.residentId}
                  id={form.id}
                  startAt={form.startAt}
                  endAt={form.endAt}
                  hourlyCost={form.hourlyCost}
                  depositCost={form.depositCost}
                  depositPaymentId={focusedReservation?.depositPaymentId}
                  bookingPaymentId={focusedReservation?.bookingPaymentId}
                  readyForPayment={focusedReservation?.readyForPayment || false}
                />
              </Grid>
            )}
          </>
        )}
        {saveError ? (
          <Grid item xs={12}>
            <div className={classes.errorText}>{`${saveError}`}</div>
          </Grid>
        ) : null}
      </Grid>

      <div className={classes.ctaContainer}>
        {/* Create mode CTA's */}
        {!props.amenityReservationId && (
          <React.Fragment>
            <RXRButton onClick={() => makeReservation(null, amenityData?.needsApproval)} isLoading={isSaving}>
              {Constant.BTN_SAVE}
            </RXRButton>
            {shouldShowSaveAndApproveButton() && (
              <RXRButton onClick={() => makeReservationAndApprove()} isLoading={isSaving} type={RXRButton.TYPE_PRIMARY}>
                {Constant.BTN_SAVE_AND_APPROVE}
              </RXRButton>
            )}
            <RXRButton type={RXRButton.TYPE_TEXT} onClick={resetForm} isLoading={isSaving}>
              {Constant.BTN_CANCEL}
            </RXRButton>
          </React.Fragment>
        )}
        {/* Edit mode needs approval CTA's */}
        {!!props.amenityReservationId && reservationNeedsApproval(form) && (
          <React.Fragment>
            <RXRButton onClick={() => saveOrApprove()} disabled={!isFormComplete(form)} isLoading={isSaving}>
              {formChanged ? Constant.BTN_SAVE_AND_APPROVE_REQUEST : Constant.BTN_APPROVE_REQUEST}
            </RXRButton>
            <RXRButton type={RXRButton.TYPE_DESTRUCTIVE} onClick={() => setShowDenyRequestDialog(true)} isLoading={isSaving}>
              {Constant.BTN_DENY_REQUEST}
            </RXRButton>
            <RXRButton type={RXRButton.TYPE_TEXT} onClick={resetForm} isLoading={isSaving}>
              {Constant.BTN_CANCEL}
            </RXRButton>
          </React.Fragment>
        )}
        {/* Edit mode past CTA's */}
        {!!props.amenityReservationId && reservationIsUpcoming(form) && (
          <React.Fragment>
            <RXRButton
              onClick={() => updateRequest()}
              disabled={!isFormComplete(form) || form.isSystemControlled || !formChanged}
              isLoading={isSaving}
            >
              {Constant.BTN_UPDATE_REQUEST}
            </RXRButton>
            {!isPlacingStaffReservation && <RXRButton onClick={() => chatResident()}>{Constant.BTN_CHAT_RESIDENT}</RXRButton>}
            <RXRButton
              type={RXRButton.TYPE_DESTRUCTIVE}
              onClick={() => setShowDeleteDialog(true)}
              disabled={form.isSystemControlled}
              isLoading={isSaving}
            >
              {Constant.BTN_DELETE}
            </RXRButton>
          </React.Fragment>
        )}
        {/* Edit mode past CTA's */}
        {isReservationPast && <RXRButton onClick={() => chatResident()}>{Constant.BTN_CHAT_RESIDENT}</RXRButton>}
      </div>
      <DiscardChangesDialog
        isOpen={showDenyRequestDialog}
        discardIcon={RXRIcon.NOT_ALLOWED}
        discardTitle={'Deny request'}
        discardMessage={'Are you sure you want to deny this request?'}
        discardButton={'Deny'}
        onCancel={() => {
          setShowDenyRequestDialog(false)
        }}
        onDiscard={() => {
          setShowDenyRequestDialog(false)
          denyRequest().then()
        }}
      />
      <DiscardChangesDialog hasChanges={formChanged} onDiscard={resetForm} />
      <ConfirmDeleteDialog
        isOpen={showDeleteDialog}
        prompt={'Are you sure you want to delete this reservation?'}
        onConfirmDelete={() => {
          setShowDeleteDialog(false)
          deleteRequest().then()
        }}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </React.Fragment>
  )
}

AmenityReservationEditor.propTypes = {
  onComplete: PropTypes.func.isRequired,
  amenityReservationId: PropTypes.string,
  initialValues: PropTypes.object,
  onUpdateForm: PropTypes.func,
  amenitiesFilter: PropTypes.func,
}

export default AmenityReservationEditor
